import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  UserPlusIcon, MagnifyingGlassIcon, UserIcon, CaretDownIcon,
  CameraIcon, UploadSimpleIcon, IdentificationCardIcon, UserCircleIcon, CheckCircleIcon,
} from "@phosphor-icons/react";

/**
 * GuestPicker — pick an existing guest OR add a new one inline without leaving the flow.
 * Props:
 *  - guests: current list of guests
 *  - value: selected guest_id
 *  - onChange(guest_id)
 *  - onGuestCreated?(guest) — invoked with the created guest
 *  - testIdPrefix
 */
export default function GuestPicker({ guests, value, onChange, onGuestCreated, testIdPrefix = "guest-picker" }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", id_type: "aadhar", id_number: "", photo: "", id_card_image: "" });
  const [saving, setSaving] = useState(false);
  const [ocring, setOcring] = useState(false);

  const selected = guests.find((g) => g.id === value);
  const filtered = q
    ? guests.filter((g) => (g.name + " " + g.phone + " " + (g.email || "")).toLowerCase().includes(q.toLowerCase()))
    : guests;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    setSaving(true);
    try {
      const r = await api.post("/guests", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        id_type: form.id_type,
        id_number: form.id_number,
      });
      const newGuest = r.data;
      // Upload photo + ID card if captured
      if (form.photo) {
        await api.post(`/guests/${newGuest.id}/photo`, { image: form.photo });
      }
      if (form.id_card_image) {
        await api.post(`/guests/${newGuest.id}/id-card`, {
          image: form.id_card_image,
          id_type: form.id_type,
          id_number: form.id_number,
        });
      }
      toast.success(`Guest '${newGuest.name}' added${form.photo && form.id_card_image ? " with KYC" : ""}`);
      onChange(newGuest.id);
      onGuestCreated?.(newGuest);
      setForm({ name: "", phone: "", email: "", id_type: "aadhar", id_number: "", photo: "", id_card_image: "" });
      setMode("existing");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add guest");
    } finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between h-10 px-3 rounded-md border border-input bg-background text-sm text-left hover:border-primary/60 transition-colors"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <span className="flex items-center gap-2 truncate">
            <UserIcon size={14} className="text-muted-foreground shrink-0" />
            {selected ? (
              <>
                <span className="truncate">{selected.name}</span>
                <span className="text-muted-foreground text-xs">· {selected.phone}</span>
              </>
            ) : (
              <span className="text-muted-foreground">Pick or add guest</span>
            )}
          </span>
          <CaretDownIcon size={12} className="opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <div className="flex border-b border-border">
          <button
            type="button"
            onClick={() => setMode("existing")}
            className={`flex-1 py-2 text-xs uppercase tracking-wider transition-colors ${mode === "existing" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}
            data-testid={`${testIdPrefix}-tab-existing`}
          >
            Existing
          </button>
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`flex-1 py-2 text-xs uppercase tracking-wider transition-colors ${mode === "new" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}
            data-testid={`${testIdPrefix}-tab-new`}
          >
            + New guest
          </button>
        </div>

        {mode === "existing" ? (
          <div className="p-2">
            <div className="relative">
              <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="Search name, phone, email…"
                className="pl-7 h-8 text-sm"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto">
              {filtered.slice(0, 40).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => { onChange(g.id); setOpen(false); setQ(""); }}
                  className={`w-full text-left px-2 py-1.5 rounded-md text-sm hover:bg-muted flex items-center justify-between ${value === g.id ? "bg-muted" : ""}`}
                  data-testid={`${testIdPrefix}-option-${g.id}`}
                >
                  <span className="truncate">{g.name}</span>
                  <span className="text-xs text-muted-foreground">{g.phone}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No guests match "{q}". Try <button type="button" onClick={() => { setMode("new"); setForm({ ...form, name: q }); }} className="text-primary underline">adding a new one</button>.
                </div>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8" required data-testid={`${testIdPrefix}-new-name`} />
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-8" required data-testid={`${testIdPrefix}-new-phone`} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-8" data-testid={`${testIdPrefix}-new-email`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">ID type</Label>
                <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aadhar">Aadhaar</SelectItem>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="driving_license">Driving license</SelectItem>
                    <SelectItem value="voter_id">Voter ID</SelectItem>
                    <SelectItem value="pan">PAN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">ID number</Label>
                <Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="h-8" data-testid={`${testIdPrefix}-new-id-number`} />
              </div>
            </div>

            {/* Inline KYC capture — mandatory before check-in but can be added later */}
            <div className="rounded-md border border-border bg-muted/20 p-2 space-y-2">
              <p className="label-overline text-[10px] flex items-center gap-1">
                <IdentificationCardIcon size={10} /> KYC (photo + Aadhaar) — required before check-in
              </p>
              <div className="grid grid-cols-2 gap-2">
                <KycMiniSlot
                  label="Photo"
                  icon={UserCircleIcon}
                  image={form.photo}
                  onCapture={(img) => setForm((f) => ({ ...f, photo: img }))}
                  testId={`${testIdPrefix}-new-photo`}
                  facing="user"
                />
                <KycMiniSlot
                  label="Aadhaar / ID"
                  icon={IdentificationCardIcon}
                  image={form.id_card_image}
                  onCapture={async (img) => {
                    setForm((f) => ({ ...f, id_card_image: img }));
                    // Run OCR against the raw image without needing a guest id — quick client-side path
                    // We only try OCR after guest is created — but we can pre-fill id_number lightly here
                    // Skipping pre-OCR to keep the flow snappy; OCR fires after save via the guest OCR endpoint.
                  }}
                  testId={`${testIdPrefix}-new-idcard`}
                  facing="environment"
                />
              </div>
              {form.id_card_image && !form.id_number && (
                <p className="text-[10px] text-muted-foreground">
                  We'll try to auto-read the ID number when you save.
                </p>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground">Photo + ID also captureable later from the guest profile.</p>
            <Button type="submit" className="w-full h-8" disabled={saving} data-testid={`${testIdPrefix}-new-submit`}>
              <UserPlusIcon size={12} className="mr-1" /> {saving ? "Adding…" : "Add & select"}
            </Button>
          </form>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Compact photo/ID capture slot for the inline "new guest" form.
 * Two actions: Camera (getUserMedia) or Upload (file picker).
 */
function KycMiniSlot({ label, icon: Icon, image, onCapture, testId, facing = "environment" }) {
  const fileRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Max 5 MB");
    const rd = new FileReader();
    rd.onload = () => onCapture(rd.result);
    rd.readAsDataURL(f);
    e.target.value = "";
  };

  return (
    <div>
      <div className={`aspect-[4/3] rounded border ${image ? "border-solid border-[hsl(var(--success))]/40" : "border-dashed border-border"} bg-muted/40 relative overflow-hidden`}>
        {image ? (
          <img src={image} alt={label} className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground text-[10px]">
            <Icon size={20} weight="duotone" className="opacity-40" />
            <span>{label}</span>
          </div>
        )}
        {image && <CheckCircleIcon size={12} weight="fill" className="absolute top-1 right-1 text-[hsl(var(--success))] bg-white rounded-full" />}
      </div>
      <div className="flex gap-1 mt-1">
        <input ref={fileRef} type="file" accept="image/*" capture={facing} className="hidden" onChange={onFile} data-testid={`${testId}-file`} />
        <button
          type="button"
          onClick={() => setCamOpen(true)}
          className="flex-1 text-[10px] h-6 border border-border rounded hover:bg-muted flex items-center justify-center gap-1"
          data-testid={`${testId}-camera`}
        >
          <CameraIcon size={10} /> Camera
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex-1 text-[10px] h-6 border border-border rounded hover:bg-muted flex items-center justify-center gap-1"
          data-testid={`${testId}-upload`}
        >
          <UploadSimpleIcon size={10} /> Upload
        </button>
      </div>
      {camOpen && <MiniCameraDialog facing={facing} onClose={() => setCamOpen(false)} onCapture={(img) => { onCapture(img); setCamOpen(false); }} />}
    </div>
  );
}

function MiniCameraDialog({ facing, onClose, onCapture }) {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing } }, audio: false });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (e) {
        toast.error("Camera unavailable — use Upload instead.");
        onClose();
      }
    })();
    return () => { cancelled = true; if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [facing]); // eslint-disable-line

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const cvs = document.createElement("canvas");
    cvs.width = v.videoWidth; cvs.height = v.videoHeight;
    cvs.getContext("2d").drawImage(v, 0, 0);
    onCapture(cvs.toDataURL("image/jpeg", 0.85));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-lg overflow-hidden max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-video bg-black">
          <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
        </div>
        <div className="p-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={snap} disabled={!ready} data-testid="guest-picker-camera-snap">
            <CameraIcon size={12} className="mr-1" /> Capture
          </Button>
        </div>
      </div>
    </div>
  );
}
